<template>
  <div>
    <ui-dialog
      :show="isOpen"
      max-width="600"
      @input="handleClose"
      class="assign-categories-dialog"
    >
      <template #header>
        <div class="assign-categories-dialog__header">
          <h3>
            {{ $t("message.reviews.categoryAssignTitle") }}
          </h3>
          <p>{{ $t("message.reviews.categoryAssignSubtitle") }}</p>
        </div>
      </template>

      <div class="assign-categories-dialog__body">
        <div class="d-flex align-center">
          <v-text-field
            v-model="categoryName"
            :label="$t('message.reviews.enterNewCategory')"
            :hide-details="true"
            clearable
            class="mt-4 mb-4"
            @keyup.enter="addCategory"
          ></v-text-field>
          <ui-button
            :disabled="!categoryName"
            class="ml-3 min-w-130"
            color="primary"
            @click="addCategory"
            >{{ $t("message.common.add") }}</ui-button
          >
        </div>
        <div class="tags-body">
          <TagsManagerListItem
            v-for="tag in categories"
            :active="setActiveCategories(tag)"
            :tag="tag"
            :key="tag.id"
            :max-characters="15"
            toggle-enabled
            @change="handleTagSelect"
          />
        </div>
      </div>
      <SectionLoader :status="isDataLoading" :opacity="0.75" />
      <template #actions>
        <div class="assign-categories-dialog__footer">
          <ui-button
            outlined
            min-width="180px"
            color="primary"
            class="mr-2"
            :disabled="isDataLoading || !isOpen"
            @click="handleClose"
          >
            {{ $t("message.reviews.cancel") }}
          </ui-button>
          <ui-button
            min-width="180px"
            color="primary"
            class="ml-2"
            :disabled="isDataLoading || !isOpen"
            @click="assignCategories"
          >
            {{ $t("message.reviews.assignCategories") }}
          </ui-button>
        </div>
      </template>
    </ui-dialog>
  </div>
</template>

<script>
import SectionLoader from "@/components/Loader/SectionLoader";
import { DIALOG_MANAGE_MODE } from "@/enums/reply-templates";
import TagsManagerListItem from "@/components/Tags/TagsManagerListItem";
import serviceHandler from "@/helpers/handlers/service";
import reviewsService from "@/services/reviews";
import tagsService from "@/services/tags";
import { GTM_EVENT_NAME } from "@/enums/gtm-events";

export default {
  name: "AssignCategoriesDialog",
  components: {
    SectionLoader,
    TagsManagerListItem
  },

  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    selectedReviewsIds: {
      type: Array,
      required: true
    }
  },

  data() {
    return {
      DIALOG_MANAGE_MODE,
      categoryName: null,
      loading: false,
      categories: null,
      selectedCategoriesIds: [],
      selectedCategories: []
    };
  },

  computed: {
    isDataLoading() {
      return this.isLoading || this.loading;
    }
  },

  async created() {
    await this.fetchCategories();
  },

  methods: {
    async addCategory() {
      this.loading = true;
      await serviceHandler(tagsService.addTag(this.categoryName));
      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.REVIEW_CATEGORIES_MANAGE,
        name: this.categoryName,
        action: "add"
      });

      await this.fetchCategories();
      this.onListUpdate();
      this.categoryName = null;
      this.loading = false;
    },
    onListUpdate() {
      this.$emit("list-update", [...this.categories]);
    },
    async fetchCategories() {
      this.categories = await serviceHandler(tagsService.getTags());
    },
    setActiveCategories(category) {
      const localCategory = this.selectedCategories.find(
        e => e.tag === category.id
      );
      if (localCategory) {
        return { ...localCategory, isActive: true };
      }
      return { ...category, isActive: false };
    },

    handleTagSelect(data) {
      const alreadySelectedTag = this.selectedCategories.find(
        e => e.tag === data.tag
      );

      if (alreadySelectedTag) {
        this.updateSelectedCategories(data);
      } else {
        this.addCategoryToSelected(data);
      }
    },

    updateSelectedCategories(data) {
      if (data.isActive) {
        this.selectedCategories = this.selectedCategories.map(e =>
          e.tag === data.tag ? { ...data } : e
        );
      } else {
        this.selectedCategories = this.selectedCategories.filter(
          e => e.tag !== data.tag
        );
      }
    },

    addCategoryToSelected(data) {
      this.selectedReviewsIds.forEach(review_id => {
        this.selectedCategories.push({
          tag: data.id,
          review: review_id,
          ...(typeof data.sentiment === "number" && {
            sentiment: data.sentiment
          })
        });
      });
    },
    async assignCategories() {
      this.loading = true;

      await serviceHandler(
        reviewsService.assignCategories(this.selectedCategories)
      );
      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.REVIEW_CATEGORIES_SELECT_DESELECT_BULK,
        action: "select",
        num_reviews: this.selectedReviewsIds.length,
        num_categories: this.selectedCategoriesIds.length
      });
      this.selectedCategoriesIds = [];

      this.$emit("assign-categories");
      this.loading = false;
    },
    handleClose() {
      this.selectedCategoriesIds = [];
      this.$emit("close");
    }
  }
};
</script>

<style lang="scss" scoped>
.assign-categories-dialog {
  &__header {
    flex: 1 1 auto;
    padding-bottom: $spacing;
    border-bottom: 1px solid $light-3;

    p {
      @include font-size(14px);
    }
  }

  &__body {
    min-height: 200px;
    position: relative;

    .tags-body {
      display: flex;
      flex-wrap: wrap;
      max-height: 300px;
      overflow-y: auto;
    }
  }

  &__footer {
    width: 100%;
    display: flex;
    justify-content: space-between;
  }
}
</style>
